<script>
  import {onMount, onDestroy} from 'svelte';
  import ImageCompare from 'image-compare-viewer';
  import 'image-compare-viewer/dist/image-compare-viewer.min.css';

  let {
    beforeSrc,
    afterSrc,
    beforeAlt = 'Before image',
    afterAlt = 'After image',
    initialSplit = 55,
    showLabels = true,
    controlColor = '#ffffff'
  } = $props();

  let container;
  let viewer;

  async function mountViewer() {
    if (!container) return;

    viewer?.destroy?.();

    // Ensure images decode before mounting the viewer to prevent dimension issues.
    const images = Array.from(container.querySelectorAll('img'));
    await Promise.all(
      images.map(img =>
        img.decode?.().catch(() => {
          /* ignore decode errors (e.g., unsupported on older browsers) */
        })
      )
    );

    viewer = new ImageCompare(container, {
      controlColor,
      controlShadow: true,
      addCircle: true,
      addCircleBlur: true,
      showLabels,
      labelOptions: {
        before: 'Before',
        after: 'After',
        onHover: false
      },
      smoothing: true,
      smoothingAmount: 120,
      hoverStart: false,
      startingPoint: initialSplit,
      fluidMode: true
    });

    viewer.mount();
  }

  onMount(() => {
    mountViewer();

    return () => {
      viewer?.destroy?.();
    };
  });
</script>

<div class="icv-shell" bind:this={container}>
  <img src={beforeSrc} alt={beforeAlt} loading="lazy" draggable="false" />
  <img src={afterSrc} alt={afterAlt} loading="lazy" draggable="false" />
</div>

<style lang="postcss">
  @reference '$theme';

  .icv-shell {
    @apply relative rounded-[36px] overflow-hidden border border-white/[0.12] bg-black/40 shadow-[0_40px_110px_rgba(13,21,32,0.5)];
    aspect-ratio: 4 / 5;
  }

  .icv-shell :global(.icv__wrapper),
  .icv-shell :global(.icv__img) {
    @apply rounded-[36px];
  }

  .icv-shell :global(img) {
    @apply h-full w-full object-cover select-none;
  }

  :global(.icv__control) {
    --icv-handle-size: 64px;
  }

  :global(.icv__circle) {
    @apply bg-white/20;
  }

  :global(.icv__label) {
    @apply text-[11px] font-semibold uppercase tracking-[0.4em] text-white/90;
    background: rgba(0, 0, 0, 0.35);
  }

  :global(.icv__arrow-wrapper svg path) {
    stroke-linecap: round;
  }

  @media (max-width: 768px) {
    .icv-shell {
      aspect-ratio: 3 / 4;
      @apply rounded-[28px];
    }

    .icv-shell :global(.icv__wrapper),
    .icv-shell :global(.icv__img) {
      @apply rounded-[28px];
    }
  }
</style>
